import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CreatableSelect from "react-select/creatable";

function ItineraryForm({ onSubmit }) {
  const [hostName, setHostName] = useState("");
  const [adults, setAdults] = useState(0);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [days, setDays] = useState(0);
  const [nights, setNights] = useState(0);
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const navigate = useNavigate();
  const [isPriceNeeded, setIsPriceNeeded] = useState(false);
  const [isLogoNeeded, setIsLogoNeeded] = useState(false);
  const [locationDetails, setLocationDetails] = useState([]);
  const [inclusions, setInclusions] = useState([]);
  const [locationsOptions, setLocationsOptions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [existingHosts, setExistingHosts] = useState([]);
  const [fileType, setFileType] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get(
          "https://api.pdf.tajgateways.com/api-v1/locations"
        );

        const formatted = res.data.map((loc) => ({
          value: loc._id,
          label: loc.title,
          fullData: loc,
        }));
        setLocationsOptions(formatted);
      } catch (err) {
        console.error("Error fetching locations:", err);
        toast.error("❌ Failed to fetch locations");
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const res = await axios.get(
          "https://api.pdf.tajgateways.com/api-v1/hotels/get-hotels"
        );
        const formatted = res.data.map((hotel) => ({
          value: hotel.name,
          label: `${hotel.name} (${hotel.address})`,
        }));
        setHotels(formatted);
      } catch (err) {
        console.error("Error fetching hotels:", err);
        toast.error("❌ Failed to fetch hotels");
      }
    };
    fetchHotels();
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      const start = new Date(dateFrom);
      const end = new Date(dateTo);
      const diffTime = end - start;
      if (diffTime >= 0) {
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDays(diffDays);
        setNights(diffDays > 0 ? diffDays - 1 : 0);
      }
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const fetchHosts = async () => {
      try {
        const res = await axios.get(
          "https://api.pdf.tajgateways.com/api-v1/users/getItineraryData"
        );

        // ✅ Extract the actual array safely
        const hostArray = res.data?.data || [];

        // ✅ Map only if it's an array
        const formattedHosts = hostArray.map((item) => ({
          value: item.hostName,
          label: item.hostName,
        }));

        setExistingHosts(formattedHosts);
      } catch (err) {
        console.error("Error fetching host list:", err);
        toast.error("❌ Failed to fetch host names");
      }
    };

    fetchHosts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const locationsArray = [];

    locationDetails.forEach((loc) => {
      // if (!loc.day || !loc.fullData) return;

      const tag = loc.sic
        ? "SIC Sightseeing"
        : loc.vip
        ? "VIP Desert Safari"
        : "PVT";

      locationsArray.push({
        day: Number(loc.day),
        title: loc.fullData.title,
        tag,
        img: loc.fullData.img,
        img1: loc.fullData.img1,
        img2: loc.fullData.img2,
        description: loc.fullData.description,
        note: loc.fullData.note,
        child_price: loc?.fullData?.child_price,
        adult_price: loc?.fullData?.adult_price,
      });
    });

    const formData = {
      type: "Itinerary",
      hostName,
      hotels: selectedHotels.map((h) => ({
        name: h.value,
        checkIn: h.checkIn,
        checkOut: h.checkOut,
        breakfastIncluded: h.breakfastIncluded || false,
        hotelPrice: h.hotelPrice,
      })),
      locationsArray,
      price: Number(price),
      fileType: fileType,
      persons: {
        adults: Number(adults),
        children: Number(children),
        infants: Number(infants),
      },
      dates: {
        from: dateFrom,
        to: dateTo,
      },
      days,
      nights,
      isPrice: isPriceNeeded,
      isLogo: isLogoNeeded,
      inclusions: inclusions.map((i) => i.value),
      exclusions: exclusions.map((e) => e.value),
    };

    console.log("🚀 Payload sending to backend:", formData);

    try {
      const res = await fetch(
        "https://api.pdf.tajgateways.com/api-v1/users/generateItinerary",
        // "http://localhost:3020/api-v1/users/generateItinerary",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await res.json();
      console.log("📬 Response:", data);

      if (data.code === 200 || data.success) {
        toast.success("✅ Form submitted successfully!");
        navigate("/");
      } else {
        toast.error("❌ Failed to submit form!");
      }

      onSubmit?.();
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error("❌ Failed to submit form, please try again!");
    } finally {
      setLoading(false);
    }
  };

  console.log("locationDetails", locationDetails);
  useEffect(() => {
    const totalLocationPrice = locationDetails.reduce((sum, l) => {
      const adultPrice = Number(l?.adult_price) || 0;
      const childPrice = Number(l?.child_price) || 0;
      const pvtPrice = l.sic ? Number(l?.sic_price) : Number(l?.pvt_price) || 0;

      const adultTotal = adults * adultPrice;
      const childTotal = children * childPrice;

      return sum + adultTotal + childTotal + pvtPrice;
    }, 0);

    const totalHotelPrice = selectedHotels.reduce(
      (sum, h) => sum + (Number(h.hotelPrice) || 0),
      0
    );

    const finalPrice = totalLocationPrice + totalHotelPrice;

    setPrice(finalPrice);
  }, [locationDetails, selectedHotels, adults, children]);

  return (
    <div style={styles.container}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div style={{ position: "absolute", top: "26px", right: "33px" }}>
        <button
          type="button"
          style={styles.dashboardBtn}
          onClick={() => navigate("/")}
        >
          📊 Dashboard
        </button>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <h3 style={styles.title}>🗺️ Itinerary Form</h3>

        {/* Host Name */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Host Name:</label>
          <CreatableSelect
            options={existingHosts}
            value={hostName ? { value: hostName, label: hostName } : null}
            onChange={async (selected) => {
              if (!selected) return;
              setHostName(selected.value);

              try {
                // fetch saved itinerary for this host
                const res = await axios.get(
                  `https://api.pdf.tajgateways.com/api-v1/users/getItineraryData?hostName=${selected.value}`
                );
                const hostData = res.data?.data?.[0]?.data;

                if (hostData) {
                  toast.info("📋 Loaded existing itinerary for this host");

                  // populate form fields
                  setAdults(hostData.persons?.adults || 0);
                  setChildren(hostData.persons?.children || 0);
                  setInfants(hostData.persons?.infants || 0);
                  setDateFrom(hostData.dates?.from || "");
                  setDateTo(hostData.dates?.to || "");
                  setDays(hostData.days || 0);
                  setNights(hostData.nights || 0);
                  setPrice(hostData.price || "");
                  setFileType(hostData.fileType || [])
                  setIsPriceNeeded(hostData.isPrice || false);
                  setIsLogoNeeded(hostData.isLogo || false);

                  // hotels
                  setSelectedHotels(
                    (hostData.hotels || []).map((h) => ({
                      value: h.name,
                      label: h.name,
                      checkIn: h.checkIn,
                      checkOut: h.checkOut,
                      hotelPrice: h.hotelPrice,
                      breakfastIncluded: h.breakfastIncluded,
                    }))
                  );

                  // locations
                  setLocationDetails(
                    (hostData.locationsArray || []).map((l, i) => ({
                      id: Date.now() + i,
                      name: l.title,
                      fullData: l,
                      day: l.day,
                      sic: l.tag === "SIC Sightseeing",
                      vip: l.tag === "VIP Desert Safari",
                      pvt_price: l?.pvt_price,
                      sic_price: l?.sic_price,
                      child_price: l?.child_price,
                      adult_price: l?.adult_price,
                    }))
                  );

                  setInclusions(
                    (hostData.inclusions || []).map((inc) => ({
                      value: inc,
                      label: inc,
                    }))
                  );
                  setExclusions(
                    (hostData.exclusions || []).map((exc) => ({
                      value: exc,
                      label: exc,
                    }))
                  );
                } else {
                  toast.info(
                    "🆕 No previous itinerary found. You can fill a new one."
                  );
                }
              } catch (err) {
                console.error("Error fetching itinerary for host:", err);
                toast.error("❌ Failed to load previous itinerary");
              }
            }}
            onInputChange={(inputValue, action) => {
              if (action.action === "input-change") setHostName(inputValue);
            }}
            onCreateOption={(inputValue) => {
              setHostName(inputValue);
              toast.success(`🆕 New host added: ${inputValue}`);
            }}
            placeholder="Select or type host name..."
            isClearable
            isSearchable
            formatCreateLabel={(inputValue) => `➕ Create "${inputValue}"`}
          />
        </div>

        {/* Persons */}
        <div style={styles.row}>
          {/* Adults */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Adults:{" "}
              <span
                style={{ fontSize: "13px", color: "#777", marginLeft: "5px" }}
              >
                (Age: 12 years & above)
              </span>
            </label>
            <input
              type="number"
              value={adults}
              onChange={(e) => {
                const val = e.target.value;
                setAdults(val === "" ? "" : Math.max(1, Number(val)));
              }}
              min="1"
              style={styles.input}
            />
          </div>

          {/* Children */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Children:{" "}
              <span
                style={{ fontSize: "13px", color: "#777", marginLeft: "5px" }}
              >
                (Age: 2 – 11 years)
              </span>
            </label>
            <input
              type="number"
              value={children}
              onChange={(e) => {
                const val = e.target.value;
                setChildren(val === "" ? "" : Math.max(0, Number(val)));
              }}
              min="0"
              style={styles.input}
            />
          </div>

          {/* Infants */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Infants:{" "}
              <span
                style={{ fontSize: "13px", color: "#777", marginLeft: "5px" }}
              >
                (Age: below 2 years)
              </span>
            </label>
            <input
              type="number"
              value={infants}
              onChange={(e) => {
                const val = e.target.value;
                setInfants(val === "" ? "" : Math.max(0, Number(val)));
              }}
              min="0"
              style={styles.input}
            />
          </div>
        </div>

        {/* Dates */}
        <div style={styles.row}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              required
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              required
              style={styles.input}
              min={dateFrom || ""}
            />
          </div>
        </div>

        {/* Days & Nights */}
        <div style={styles.row}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Days:</label>
            <input
              type="number"
              value={days}
              readOnly
              style={styles.inputDisabled}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nights:</label>
            <input
              type="number"
              value={nights}
              readOnly
              style={styles.inputDisabled}
            />
          </div>
        </div>

        {/* Multi Hotel Select */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Hotels:</label>
          <Select
            options={hotels}
            value={selectedHotels}
            onChange={(selected) => {
              const updated = selected.map((h) => {
                const existing = selectedHotels.find(
                  (sh) => sh.value === h.value
                );
                return (
                  existing || {
                    ...h,
                    checkIn: "",
                    checkOut: "",
                    breakfastIncluded: false, // new field
                  }
                );
              });
              setSelectedHotels(updated);
            }}
            placeholder="Search and select hotels..."
            isSearchable
            isMulti
          />
        </div>

        {/* Check-In / Check-Out Dates + Breakfast for each hotel */}
        {selectedHotels.map((hotel, idx) => (
          <div
            key={idx}
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              borderRadius: "6px",
              marginTop: "10px",
            }}
          >
            <h4>{hotel.label}</h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
              <div>
                <label>Check-In:</label>
                <input
                  type="date"
                  value={hotel.checkIn}
                  onChange={(e) => {
                    const updated = [...selectedHotels];
                    updated[idx].checkIn = e.target.value;
                    setSelectedHotels(updated);
                  }}
                />
              </div>

              <div>
                <label>Check-Out:</label>
                <input
                  type="date"
                  value={hotel.checkOut}
                  min={hotel.checkIn || ""}
                  onChange={(e) => {
                    const updated = [...selectedHotels];
                    updated[idx].checkOut = e.target.value;
                    setSelectedHotels(updated);
                  }}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Hotel Price:</label>
                <input
                  type="number"
                  value={hotel?.hotelPrice || ""}
                  onChange={(e) => {
                    const value = Number(e.target.value) || 0; // Ensure numeric value

                    // Create a new array with updated hotel price
                    const updated = selectedHotels.map((h, i) =>
                      i === idx ? { ...h, hotelPrice: value } : h
                    );

                    setSelectedHotels(updated);
                  }}
                  placeholder="Enter Hotel Price"
                  required
                  style={styles.input}
                />
              </div>

              {/* Breakfast Included Checkbox */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="checkbox"
                  checked={hotel.breakfastIncluded}
                  onChange={(e) => {
                    const updated = [...selectedHotels];
                    updated[idx].breakfastIncluded = e.target.checked;
                    setSelectedHotels(updated);
                  }}
                />
                <label>Breakfast Included</label>
              </div>
            </div>
          </div>
        ))}

        {/* Locations */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Locations:</label>
          <Select
            options={locationsOptions}
            onChange={(selected) => {
              if (selected) {
                const newEntry = {
                  id: Date.now(),
                  locationId: selected.value,
                  name: selected.label,
                  fullData: selected.fullData,
                  sic: false,
                  vip: false,
                  day: "",
                  pvt_price: selected?.fullData?.pvt_price,
                  sic_price: selected?.fullData?.sic_price,
                  child_price: selected?.fullData?.child_price,
                  adult_price: selected?.fullData?.adult_price,
                };
                setLocationDetails((prev) => [...prev, newEntry]);
              }
            }}
            placeholder="Select a location to add"
            isSearchable
          />

          {locationDetails.length > 0 && (
            <div style={{ marginTop: "15px" }}>
              {locationDetails.map((loc) => (
                <div
                  key={loc.id}
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    padding: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <h4>{loc.name}</h4>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={loc.sic}
                        onChange={() => {
                          setLocationDetails((prev) => {
                            // 1️⃣ Update locationDetails first
                            const updated = prev.map((l) =>
                              l.id === loc.id
                                ? { ...l, sic: !l.sic, vip: false }
                                : l
                            );

                            return updated;
                          });
                        }}
                      />{" "}
                      SIC
                    </label>

                    {loc.name === "Desert Safari" && (
                      <label>
                        <input
                          type="checkbox"
                          checked={loc.vip}
                          onChange={() =>
                            setLocationDetails((prev) => {
                              const updated = prev.map((l) =>
                                l.id === loc.id
                                  ? { ...l, vip: !l.vip, sic: false }
                                  : l
                              );

                              return updated;
                            })
                          }
                        />{" "}
                        VIP
                      </label>
                    )}

                    <label>
                      Day:
                      <input
                        type="number"
                        min="1"
                        max={days + 1}
                        value={loc.day || ""}
                        onChange={(e) =>
                          setLocationDetails((prev) =>
                            prev.map((l) =>
                              l.id === loc.id
                                ? { ...l, day: e.target.value }
                                : l
                            )
                          )
                        }
                        style={{ width: "70px", marginLeft: "5px" }}
                      />
                    </label>

                    <button
                      type="button"
                      onClick={() =>
                        setLocationDetails((prev) =>
                          prev.filter((l) => l.id !== loc.id)
                        )
                      }
                      style={{
                        background: "gray",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        cursor: "pointer",
                      }}
                    >
                      ✕ Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Price */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Price (AED):</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Enter price in AED"
            required
            style={styles.input}
          />
        </div>

        {/* Inclusions Dropdown */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Inclusions:</label>
          <CreatableSelect
            isMulti
            options={[
              { value: "Transfers", label: "Transfers" },
              { value: "Visafees", label: "Visafees" },
              { value: "Two Dinners", label: "Two Dinners" },
              {
                value:
                  "Sightseeing and accommodation with breakfast as per the above-mentioned itinerary",
                label:
                  "Sightseeing and accommodation with breakfast as per the above-mentioned itinerary",
              },
              {
                value: "Assistance of the tour before the trip",
                label: "Assistance of the tour before the trip",
              },
            ]}
            value={inclusions}
            onChange={(selected) => setInclusions(selected)}
            placeholder="Select  or type inclusions..."
            isSearchable
          />
        </div>

        {/* Exclusions Dropdown */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Exclusions:</label>
          <CreatableSelect
            isMulti
            options={[
              { value: "Airfares", label: "Airfares" },
              { value: "GST 5%", label: "GST 5%" },
              { value: "TCS 5% / 20%", label: "TCS 5% / 20%" },
              {
                value: "Compulsory tips wherever applicable",
                label: "Compulsory tips wherever applicable",
              },
              {
                value:
                  "Tourist City Tax which needs to be paid directly at Hotel by Customer",
                label:
                  "Tourist City Tax which needs to be paid directly at Hotel by Customer",
              },
              {
                value: 'Anything not mentioned in the "Inclusions"',
                label: 'Anything not mentioned in the "Inclusions"',
              },
              {
                value:
                  "Expenses of personal nature such as laundry, mini-bar, telephone charges, food & drink not part of the package",
                label:
                  "Expenses of personal nature such as laundry, mini-bar, telephone charges, food & drink not part of the package",
              },
            ]}
            value={exclusions}
            onChange={(selected) => setExclusions(selected)}
            placeholder="Select or type exclusions..."
            isSearchable
          />
        </div>

        {/* Extra Checkboxes */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={isPriceNeeded}
              onChange={(e) => setIsPriceNeeded(e.target.checked)}
            />{" "}
            Price Needed
          </label>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={isLogoNeeded}
              onChange={(e) => setIsLogoNeeded(e.target.checked)}
            />{" "}
            Logo Needed
          </label>
        </div>

        <div style={{marginBottom:"20px"}}>
          <p style={styles.label}>Selected File Type: {fileType}</p>
          <label style={styles.label}>
            <input
              type="radio"
              name="fileType"
              value="pdf"
              checked={fileType === "pdf"}
              onChange={(e) => setFileType(e.target.value)}
            />
            PDF
          </label>

          <label style={styles.label}>
            <input
              type="radio"
              name="fileType"
              value="docx"
              checked={fileType === "docx"}
              onChange={(e) => setFileType(e.target.value)}
            />
            DOCX
          </label>
        </div>

        <button style={styles.button} type="submit" disabled={loading}>
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <div style={styles.loader}></div>
              Submitting...
            </div>
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "80vh",
    padding: "20px",
  },
  form: {
    background: "#fff",
    padding: "53px",
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    maxWidth: "700px",
    width: "100%",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
    marginTop: "86px",
    boxSizing: "border-box",
  },
  title: {
    fontSize: "22px",
    marginBottom: "20px",
    color: "#2c3e50",
    textAlign: "center",
  },
  dashboardBtn: {
    background: "#3498db",
    color: "#fff",
    padding: "8px 14px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  formGroup: {
    marginBottom: "15px",
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: "6px",
    fontWeight: "600",
    fontSize: "14px",
    color: "#34495e",
  },
  input: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "14px",
    outline: "none",
    transition: "0.3s",
    width: "100%",
    boxSizing: "border-box",
  },
  inputDisabled: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    background: "#f9f9f9",
    color: "#7f8c8d",
    width: "100%",
    boxSizing: "border-box",
  },
  row: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
  },
  checkboxContainer: {
    maxHeight: "200px",
    overflowY: "scroll",
    border: "1px solid #ccc",
    padding: "10px",
    borderRadius: "6px",
    background: "#fafafa",
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  button: {
    background: "#3498db",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "500",
    transition: "0.3s",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  loader: {
    border: "2px solid #f3f3f3",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    width: "14px",
    height: "14px",
    animation: "spin 1s linear infinite",
  },
};

// Inject responsive behavior via <style> (media queries)
const responsiveStyle = document.createElement("style");
responsiveStyle.type = "text/css";
responsiveStyle.innerText = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 📱 Mobile responsiveness */
@media (max-width: 768px) {
  form {
    padding: 25px !important;
    width: 100% !important;
  }

  .Select__control {
    font-size: 13px !important;
  }

  input, select {
    font-size: 13px !important;
  }

  h3 {
    font-size: 18px !important;
  }

  label {
    font-size: 13px !important;
  }

  .react-select__menu {
    font-size: 13px !important;
  }

  div[style*="display: flex"][style*="gap: 15px"] {
    flex-direction: column !important;
    gap: 10px !important;
  }

  button {
    width: 100% !important;
    padding: 12px !important;
    font-size: 14px !important;
  }

  h4 {
    font-size: 15px !important;
  }
}

/* 📱 Extra small screens */
@media (max-width: 480px) {
  form {
    padding: 20px !important;
  }

  input, select, .Select__control {
    font-size: 12px !important;
    padding: 8px !important;
  }

  h3 {
    font-size: 16px !important;
  }

  label {
    font-size: 12px !important;
  }

  div[style*="display: flex"][style*="gap: 15px"] {
    flex-direction: column !important;
  }

  button {
    font-size: 13px !important;
    padding: 10px !important;
  }

  h4 {
    font-size: 14px !important;
  }
}
`;
document.head.appendChild(responsiveStyle);

export default ItineraryForm;
